/**
 * №53 · 53.4 — HANDLED: THE RECEIPT. Direction B, as David ruled it (E-31).
 *
 * Three ways to read this day were drawn from the shipped shell and put in front
 * of him (`docs/53.4-handled-options/`); B won. Inside a band, what wanted
 * something from you renders at full size, and the repetitive bulk folds to one
 * counted line that expands. A is not built, C is not built, and E-23's standing
 * reservation on this surface is spent — it was drawn three ways and chosen.
 *
 * ## What this page will not say
 *
 * The approved mockup gives every line a right-hand fact (`−$288`, `0.4h`,
 * `SharePoint`) and every band an hours total. **A receipt carries none of them**
 * — see `handled-model.ts` and E-32. So the right column is *what it touched*
 * where there is one, absent otherwise, and the band meta is a count. Two gates
 * in `test:handled-53.4` fail the build if an hours or money figure is ever
 * computed here.
 *
 * Likewise the bands are CLOCK bands and each says so, because nothing in the
 * product records when a lawyer started a piece of work. `axis` and `source`
 * come off the server, so the day a start time exists this file changes nothing.
 *
 * ## Answered today lives here now (E-27)
 *
 * David ruled that the rail wins and the "Answered today" strip comes off `/app`.
 * It is not deleted — a receipt is exactly where "what you already dealt with"
 * belongs, and it is the SAME `AnsweredStrip` component the matter cockpit uses.
 * §22.6's assertion that there is only ever one implementation of that history is
 * re-pointed here rather than dropped.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";
import { AnsweredStrip } from "../../components/collab/AnsweredStrip";
import { todayStartISO } from "../../components/collab/answered-window";
// RC1-1 — the hash → persona mapping lives in exactly one file. A hire is one
// colour on every surface, so this reads the shared derivation rather than
// choosing an avatar fill of its own.
import { agentPersonaFromId, personaFill } from "../../lib/agent-persona";
import {
  affordanceFor, bandMeta, clockOf, foldOf, headlineOf, ledeOf, loudOf,
  type HandledBand, type HandledLine,
} from "./handled-model";

const HANDLED_KEY = ["handled-day"] as const;

function Initials({ line }: { line: HandledLine }) {
  const name = line.actor_name ?? "—";
  const initials = name.split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "—";
  const fill = personaFill(agentPersonaFromId(line.actor_id));
  return (
    <span className="hd-who" style={{ background: fill }} title={name}>
      {initials}
    </span>
  );
}

function LineCard({ line }: { line: HandledLine }) {
  const aff = affordanceFor(line);
  // The edge's colour is the CLASS, not a per-verb decision: red for a refusal,
  // amber for something that has left the firm, accent for anything still undoable.
  const tone = !line.ok ? "hd-card--blocked"
    : line.undo.klass === "irreversible" ? "hd-card--outbound"
    : line.undo.klass === "reversible" ? "hd-card--open" : "";
  return (
    <div className={`hd-card ${tone}`} data-testid="handled-line" data-verb={line.verb}>
      <span className="hd-card__edge" aria-hidden="true" />
      <div className="hd-card__main">
        <div className="hd-card__h">{headlineOf(line)}</div>
        {!line.ok && (
          <div className="hd-card__d hd-card__d--blocked">
            <b>blocked</b>{line.summary ? " — it could not finish, and said so" : ""}
          </div>
        )}
        <div className="hd-card__m">
          <span>{clockOf(line.at)}</span>
          <Initials line={line} />
          <span>{line.actor_name ?? "unattributed"}</span>
          <span className="hd-verb">{line.verb}</span>
          {/* What it touched — present only when the receipt actually carries one.
              There is no hours or money figure here and there cannot be: see the
              file header and E-32. */}
          {line.ref ? <span className="hd-ref">{line.ref}</span> : null}
        </div>
      </div>
      <div className="hd-card__act">
        <span className={`hd-act${!line.ok ? " hd-act--warn" : ""}`}>{aff.label}</span>
        {aff.note ? <span className="hd-act__note">{aff.note}</span> : null}
      </div>
    </div>
  );
}

function Band({ band }: { band: HandledBand }) {
  const [open, setOpen] = useState(false);
  const loud = loudOf(band.lines);
  const fold = foldOf(band.lines);
  const folded = band.lines.filter((l) => !l.consequential);
  return (
    <section className="hd-band" data-testid="handled-band">
      <header className="hd-band__h">
        <span className="hd-band__t">{band.title}</span>
        {/* A clock band's title IS its span, so printing both renders
            "08:20–08:31 · 08:20–08:31". The span shows only when the title is
            something else — today that is "Before you arrived", and tomorrow a
            matter name from a real start time. */}
        {band.title !== band.span ? <span className="hd-band__s">· {band.span}</span> : null}
        {/* D-CR16 — the band names its own source, verbatim from the server. The
            surface never writes this string: the day it stops being the clock,
            the server says so and this renders the new one. */}
        <span className="hd-band__src" data-testid="handled-band-source">{band.source}</span>
        <span className="hd-band__n">{bandMeta(band)}</span>
      </header>

      {loud.map((l) => <LineCard key={l.id} line={l} />)}

      {fold && (
        <>
          <button
            type="button"
            className="hd-fold"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid="handled-fold"
          >
            <span className="hd-fold__n">{fold.count} more</span>
            <span className="hd-fold__v">
              {fold.verbs.join(", ")}
              {fold.any_actionable
                ? " — one of these still wants something"
                : " — nothing raised, nothing waiting"}
            </span>
            <span className="hd-fold__go">{open ? "collapse" : "expand"}</span>
          </button>
          {open && folded.map((l) => <LineCard key={l.id} line={l} />)}
        </>
      )}
    </section>
  );
}

export function HandledPage() {
  const day = useQuery({
    queryKey: HANDLED_KEY,
    queryFn: () => mcpClient.getHandledDay(),
    staleTime: 15_000,
  });

  const d = day.data;

  return (
    <div className="screen" data-testid="handled-page">
      <div className="viewhead">
        <h1>Handled</h1>
        <p className="lead" data-testid="handled-lede">
          {day.isLoading ? "Reading the day's receipts…" : d ? ledeOf(d) : "The receipt could not be read."}
        </p>
      </div>

      {/* An empty day is a real answer, not a failure state — and it is drawn as
          one sentence rather than as an empty heading with nothing under it. */}
      {d && d.totals.lines === 0 && (
        <p className="hd-empty" data-testid="handled-empty">
          Nothing has run for you today. This page fills itself as your crew works — every verb they
          fire lands here, including the ones the walls refused.
        </p>
      )}

      {d?.bands.map((b) => <Band key={b.key} band={b} />)}

      {/* The lawyer's own recorded time — EVENTS on the page, not the band axis.
          They cannot be the axis: a time entry has a date and a duration and no
          start time, so it cannot be placed against a clock (E-32). */}
      {d && d.own_time.length > 0 && (
        <section className="hd-band" data-testid="handled-own-time">
          <header className="hd-band__h">
            <span className="hd-band__t">Your own time, recorded today</span>
            <span className="hd-band__src">from your time entries — no start times recorded</span>
            <span className="hd-band__n">{d.own_time.length === 1 ? "1 entry" : `${d.own_time.length} entries`}</span>
          </header>
          {d.own_time.map((t) => (
            <div className="hd-card" key={t.id}>
              <span className="hd-card__edge" aria-hidden="true" />
              <div className="hd-card__main">
                <div className="hd-card__h">
                  {t.narrative ?? "Time recorded"}
                  {typeof t.units === "number" ? ` — ${t.units}h` : ""}
                </div>
                <div className="hd-card__m">
                  <span className="hd-verb">record_time</span>
                  {t.matter ? <span className="hd-ref">{t.matter}</span> : null}
                  {!t.billable ? <span>non-billable</span> : null}
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* №53 · E-27 — "Answered today" moved here from `/app`. Same component the
          matter cockpit renders: two implementations of a history are two
          histories. Hides entirely on a day with no calls. */}
      <AnsweredStrip
        heading="Answered today"
        note="what you have already dealt with — from the record, not this page"
        since={todayStartISO()}
        hideWhenEmpty
      />

      {d && (
        <p className="hd-foot">
          Everything here is inspectable forever. What is still reversible says how long you have;
          what is not says why. {d.totals.refused > 0
            ? `${d.totals.refused === 1 ? "One verb was" : `${d.totals.refused} verbs were`} refused today and ${d.totals.refused === 1 ? "is" : "are"} on the page — a receipt that recorded only what succeeded would be a highlights reel.`
            : "No verb was refused today."}
        </p>
      )}
    </div>
  );
}
